import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getCurrentUserRole } from 'features/auth/ducks/selectors'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import documentAccessAPI, { DOCUMENT_ACCESS_CHANGED } from 'features/documentAccess/documentAccessAPI'
import RequestAccessDialog from 'features/documentAccess/RequestAccessDialog'
import { BORDER, INK, LINK, MUTED, PrimaryButton } from 'features/actions/workspace/ui'

const LockedDocumentsBanner = ({ bpId }) => {
  const [files, setFiles] = useState(null)
  const [open, setOpen] = useState(false)
  const canSeeRequests = useSelector(getCurrentUserRole) === 'user'

  const load = useCallback(() => {
    if (!bpId) return
    documentAccessAPI.lockedFiles(bpId)
      .then(res => setFiles(res?.data?.data || []))
      .catch(() => setFiles([]))
  }, [bpId])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    window.addEventListener(DOCUMENT_ACCESS_CHANGED, load)
    return () => window.removeEventListener(DOCUMENT_ACCESS_CHANGED, load)
  }, [load])

  if (!files || !files.length) return null

  const askable = files.filter(f => {
    const r = f.accessRequest
    return !r || r.status === 'withdrawn' || r.status === 'approved' || (r.status === 'refused' && !r.canAskAgainAt)
  })
  const waiting = files.filter(f => f.accessRequest?.status === 'pending').length
  const fromSuccession = files.some(f => f.lockedBySuccession)

  return (
    <Banner role='region' aria-label='Locked documents' data-tour='locked-documents-banner'>
      <Icon><LockOutlinedIcon aria-hidden='true' /></Icon>
      <Text>
        <Heading>{files.length} document{files.length === 1 ? ' is' : 's are'} locked for you</Heading>
        <Body>
          {fromSuccession
            ? 'They were added before this Building Passport changed hands, so they still belong to the previous owner\'s organisation. '
            : 'You can see they exist, but you need permission to open them. '}
          {'Ask for access and the people who decide for each document, usually the organisation\'s Super Admins or a nominated approver, will approve or refuse it.'}
          {waiting > 0 && <> {waiting} request{waiting === 1 ? ' is' : 's are'} waiting for a decision.{canSeeRequests && <> <SeeLink to='/document-access-requests?box=mine'>See your requests</SeeLink></>}</>}
        </Body>
      </Text>
      {askable.length > 0 && (
        <PrimaryButton type='button' onClick={() => setOpen(true)}>
          Request access{askable.length > 1 ? ` (${askable.length})` : ''}
        </PrimaryButton>
      )}
      <RequestAccessDialog
        open={open}
        bpId={bpId}
        files={askable.map(f => ({ id: f.id, name: f.name, uploaderName: f.uploaderName, location: f.location }))}
        onClose={() => setOpen(false)}
        onDone={load}
      />
    </Banner>
  )
}

export default LockedDocumentsBanner

const Banner = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin: 0 0 16px;
  padding: 14px 16px;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  background: #fafafa;
`

const Icon = styled.span`
  display: inline-flex;
  color: ${MUTED};
`

const Text = styled.div`
  flex: 1 1 280px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const Heading = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${INK};
`

const Body = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${MUTED};
`

const SeeLink = styled(Link)`
  color: ${LINK};
  font-weight: 600;
`
